import api from './api';
import { API_ENDPOINTS } from './config';

/**
 * Livestream Service
 * Handles fetching livestream projects
 */

/**
 * Fetch livestream projects based on tab
 * @param {string} tab - 'upcoming' | 'completed'
 * @param {number} limit - Number of items to fetch
 * @param {number} page - Page number
 * @returns {Promise<Object>} { success, data, error }
 */
export const getLivestreamProjects = async (tab = 'upcoming', page = 1, limit = 50) => {
    try {
        const response = await api.get(API_ENDPOINTS.LIVESTREAM.PROJECTS, {
            params: {
                tab,
                page,
                limit
            },
            headers: {
                'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
                "Content-Type": "application/json",
                "Accept": "application/json",
            },
        });
        return response;
    } catch (error) {
        console.error('Get Livestream Projects Error:', error);
        return {
            success: false,
            data: null,
            error: 'Failed to fetch livestream projects',
        };
    }
};
/**
 * Create or Get Stream for a project (IVS only)
 * @param {number|string} projectId - stream_project_booking_id
 * @returns {Promise<Object>} { success, data, error }
 */
export const createOrGetStream = async (projectId) => {
    try {
        const url = API_ENDPOINTS.LIVESTREAM.CREATE_STREAM.replace(':id', projectId);
        const response = await api.post(url, {});
        return response;
    } catch (error) {
        console.error('Create/Get Stream Error:', error);
        return {
            success: false,
            data: null,
            error: 'Failed to initialize stream',
        };
    }
};
/**
 * Start MediaLive (multistream)
 * @param {number|string} streamId - stream_id
 * @returns {Promise<Object>} { success, data, error }
 */
export const startMediaLive = async (streamId) => {
    try {
        const url = API_ENDPOINTS.LIVESTREAM.START_MEDIALIVE.replace(':id', streamId);
        const response = await api.post(url, {});
        return response;
    } catch (error) {
        console.error('Start MediaLive Error:', error);
        return {
            success: false,
            data: null,
            error: 'Failed to start livestream',
        };
    }
};

/**
 * Get project details for livestream management
 * @param {number|string} projectId - stream_project_booking_id
 * @returns {Promise<Object>} { success, data, error }
 */
export const getProjectDetails = async (projectId) => {
    try {
        const url = API_ENDPOINTS.LIVESTREAM.PROJECT_DETAILS.replace(':id', projectId);
        const response = await api.get(url);
        return response;
    } catch (error) {
        console.error('Get Project Details Error:', error);
        return {
            success: false,
            data: null,
            error: 'Failed to fetch project details',
        };
    }
};

/**
 * Get stream by ID
 * @param {number|string} streamId - stream_id
 * @returns {Promise<Object>} { success, data, error }
 */
export const getStreamById = async (streamId) => {
    try {
        const url = API_ENDPOINTS.LIVESTREAM.GET_STREAM.replace(':id', streamId);
        const response = await api.get(url);
        return response;
    } catch (error) {
        console.error('Get Stream Error:', error);
        return {
            success: false,
            data: null,
            error: 'Failed to fetch stream details',
        };
    }
};

/**
 * Get MediaLive details (OBS settings / input endpoints)
 * @param {number|string} streamId - stream_id
 * @returns {Promise<Object>} { success, data, error }
 */
export const getMediaLiveDetails = async (streamId) => {
    try {
        const url = API_ENDPOINTS.LIVESTREAM.MEDIALIVE_DETAILS.replace(':id', streamId);
        const response = await api.get(url);
        return response;
    } catch (error) {
        console.error('Get MediaLive Details Error:', error);
        return {
            success: false,
            data: null,
            error: 'Failed to fetch MediaLive settings',
        };
    }
};

/**
 * Get stream status (IVS + MediaLive status)
 * @param {number|string} streamId - stream_id
 * @returns {Promise<Object>} { success, data, error }
 */
export const getStreamStatus = async (streamId) => {
    try {
        const url = API_ENDPOINTS.LIVESTREAM.STREAM_STATUS.replace(':id', streamId);
        const response = await api.get(url);
        return response;
    } catch (error) {
        console.error('Get Stream Status Error:', error);
        return {
            success: false,
            data: null,
            error: 'Failed to fetch stream status',
        };
    }
};

/**
 * Get stream health
 * @param {number|string} streamId - stream_id
 * @returns {Promise<Object>} { success, data, error }
 */
export const getStreamHealth = async (streamId) => {
    try {
        const url = API_ENDPOINTS.LIVESTREAM.STREAM_HEALTH.replace(':id', streamId);
        const response = await api.get(url);
        return response;
    } catch (error) {
        console.error('Get Stream Health Error:', error);
        return {
            success: false,
            data: null,
            error: 'Failed to fetch stream health',
        };
    }
};

/**
 * Stop stream (stops & deletes MediaLive + rotates IVS key)
 * @param {number|string} streamId - stream_id
 * @returns {Promise<Object>} { success, data, error }
 */
export const stopStream = async (streamId) => {
    try {
        const url = API_ENDPOINTS.LIVESTREAM.STOP_STREAM.replace(':id', streamId);
        const response = await api.post(url, {});
        return response;
    } catch (error) {
        console.error('Stop Stream Error:', error);
        return {
            success: false,
            data: null,
            error: 'Failed to stop stream',
        };
    }
};

/**
 * Get platform destinations (connections)
 * @param {number|string} streamId - stream_id
 * @returns {Promise<Object>} { success, data, error }
 */
export const getDestinations = async (streamId) => {
    try {
        const url = API_ENDPOINTS.LIVESTREAM.DESTINATIONS.replace(':id', streamId);
        const response = await api.get(url);
        return response;
    } catch (error) {
        console.error('Get Destinations Error:', error);
        return {
            success: false,
            data: null,
            error: 'Failed to fetch platform connections',
        };
    }
};

/**
 * Add platform destination (connection)
 * @param {number|string} streamId - stream_id
 * @param {string} platform - Platform name (e.g., 'YOUTUBE', 'TWITCH')
 * @param {string} rtmpUrl - RTMP URL for the platform
 * @returns {Promise<Object>} { success, data, error }
 */
export const addDestination = async (streamId, platform, rtmpUrl) => {
    try {
        const url = API_ENDPOINTS.LIVESTREAM.DESTINATIONS.replace(':id', streamId);
        const response = await api.post(url, {
            platform,
            rtmp_url: rtmpUrl,
        });
        return response;
    } catch (error) {
        console.error('Add Destination Error:', error);
        return {
            success: false,
            data: null,
            error: 'Failed to add platform connection',
        };
    }
};

/**
 * Update platform destination
 * @param {number|string} destinationId - destination_id
 * @param {string} rtmpUrl - New RTMP URL
 * @param {number} isActive - 1 for active, 0 for inactive
 * @returns {Promise<Object>} { success, data, error }
 */
export const updateDestination = async (destinationId, rtmpUrl, isActive) => {
    try {
        const url = API_ENDPOINTS.LIVESTREAM.DESTINATION_BY_ID.replace(':id', destinationId);
        const response = await api.put(url, {
            rtmp_url: rtmpUrl,
            is_active: isActive,
        });
        return response;
    } catch (error) {
        console.error('Update Destination Error:', error);
        return {
            success: false,
            data: null,
            error: 'Failed to update platform connection',
        };
    }
};

/**
 * Delete platform destination (soft delete)
 * @param {number|string} destinationId - destination_id
 * @returns {Promise<Object>} { success, data, error }
 */
export const deleteDestination = async (destinationId) => {
    try {
        const url = API_ENDPOINTS.LIVESTREAM.DESTINATION_BY_ID.replace(':id', destinationId);
        const response = await api.delete(url);
        return response;
    } catch (error) {
        console.error('Delete Destination Error:', error);
        return {
            success: false,
            data: null,
            error: 'Failed to delete platform connection',
        };
    }
};
